/**
 * SeletoMarket Cart Drawer Ajax Engine
 * Bulletproof implementation — no dependency on class state, always queries DOM fresh.
 */

class CartDrawer {
  constructor() {
    this.isOpen = false;
    this.isAnimating = false;
    this.freeShippingThreshold = window.ThemeSettings?.freeShippingThreshold ?? 0;
    this.initEvents();
  }

  // Helper: always grab fresh references from DOM (Shopify section rendering can replace them)
  getPanel() { return document.getElementById('cart-drawer-panel'); }
  getOverlay() { return document.getElementById('cart-drawer-overlay'); }

  initEvents() {
    // Global delegated click listener for cart triggers, close buttons, overlay and quick add
    document.addEventListener('click', (e) => {
      // 1. Open drawer triggers (any element with data-cart-drawer-trigger)
      const trigger = e.target.closest('[data-cart-drawer-trigger]') || e.target.closest('.cart-drawer-trigger');
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        this.open();
        return;
      }

      // 2. Close button
      const closeBtn = e.target.closest('#cart-drawer-close') || e.target.closest('[data-cart-drawer-close]');
      if (closeBtn) {
        e.preventDefault();
        this.close();
        return;
      }

      // 3. Overlay click to close
      if (e.target.id === 'cart-drawer-overlay') {
        e.preventDefault();
        this.close();
        return;
      }

      // 4. Quick add to cart buttons across the site
      const quickAddBtn = e.target.closest('.btn-quick-add') || e.target.closest('.btn-sticky-add');
      if (quickAddBtn) {
        e.preventDefault();
        const variantId = quickAddBtn.getAttribute('data-variant-id');
        const qty = quickAddBtn.getAttribute('data-quantity') || 1;
        if (variantId) {
          this.addToCart(variantId, parseInt(qty, 10), quickAddBtn);
        }
      }
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if ((e.key === 'Escape' || e.keyCode === 27) && this.isOpen) {
        this.close();
      }
    });

    // Universal delegated submit for ANY form submitting to /cart/add
    document.addEventListener('submit', (e) => {
      const productForm = e.target.closest('form[action*="/cart/add"]');
      if (productForm) {
        e.preventDefault();
        const formData = new FormData(productForm);
        this.addFormDataToCart(formData);
      }
    });

    // Shipping protection checkbox listener (Pre-checked by default)
    document.addEventListener('change', async (e) => {
      if (e.target.id === 'cart-protection-checkbox') {
        const variantId = e.target.getAttribute('data-protection-variant');
        if (e.target.checked) {
          sessionStorage.removeItem('protection_declined');
          if (variantId && variantId.trim() !== '') {
            await this.toggleProtection(true, variantId);
          } else {
            await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                attributes: {
                  'Garantie Livraison Sérénité': 'Oui (+2,99 €)'
                }
              })
            }).catch(console.error);
          }
        } else {
          sessionStorage.setItem('protection_declined', 'true');
          if (variantId && variantId.trim() !== '') {
            await this.toggleProtection(false, variantId);
          } else {
            await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                attributes: {
                  'Garantie Livraison Sérénité': 'Non'
                }
              })
            }).catch(console.error);
          }
        }
      }
    });
  }

  open() {
    // Guard against re-entrance (double-click, double-trigger)
    if (this.isOpen || this.isAnimating) return;
    this.isAnimating = true;

    const panel = this.getPanel();
    const overlay = this.getOverlay();

    if (!panel || !overlay) {
      console.warn('[CartDrawer] Panel or overlay not found in DOM.');
      this.isAnimating = false;
      return;
    }

    // Force the Shopify section wrapper to not clip our fixed elements
    const sectionWrapper = panel.closest('.shopify-section');
    if (sectionWrapper) {
      sectionWrapper.style.setProperty('position', 'static', 'important');
      sectionWrapper.style.setProperty('overflow', 'visible', 'important');
    }

    // Show overlay
    overlay.style.setProperty('opacity', '1', 'important');
    overlay.style.setProperty('pointer-events', 'auto', 'important');
    overlay.style.setProperty('visibility', 'visible', 'important');
    overlay.classList.add('is-active', 'is-open');

    // Slide panel in
    panel.style.setProperty('transform', 'translateX(0)', 'important');
    panel.style.setProperty('visibility', 'visible', 'important');
    panel.classList.add('is-active', 'is-open');

    // Lock body scroll
    document.body.style.overflow = 'hidden';

    this.isOpen = true;
    this.isAnimating = false;

    // Refresh cart contents
    this.refreshCart();
  }

  close() {
    if (!this.isOpen) return;

    const panel = this.getPanel();
    const overlay = this.getOverlay();

    if (panel) {
      panel.classList.remove('is-active', 'is-open');
      panel.style.setProperty('transform', 'translateX(100%)', 'important');
      // After transition, hide
      setTimeout(() => {
        panel.style.removeProperty('visibility');
      }, 400);
    }

    if (overlay) {
      overlay.classList.remove('is-active', 'is-open');
      overlay.style.setProperty('opacity', '0', 'important');
      overlay.style.setProperty('pointer-events', 'none', 'important');
      // After transition, hide
      setTimeout(() => {
        overlay.style.removeProperty('visibility');
      }, 400);
    }

    document.body.style.overflow = '';
    this.isOpen = false;
  }

  async addToCart(variantId, quantity = 1, buttonEl = null) {
    const originalText = buttonEl ? buttonEl.innerHTML : '';
    if (buttonEl) {
      buttonEl.disabled = true;
      buttonEl.innerHTML = '<span class="animate-spin inline-block mr-1">⏳</span> Adicionando...';
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: quantity })
      });

      if (!response.ok) throw new Error('Add to cart failed');

      await this.refreshCart();
      this.open();
    } catch (err) {
      console.error('Error adding to cart:', err);
      alert('Não foi possível adicionar o produto ao carrinho agora.');
    } finally {
      if (buttonEl) {
        buttonEl.disabled = false;
        buttonEl.innerHTML = originalText;
      }
    }
  }

  async addFormDataToCart(formData) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Add to cart failed');

      await this.refreshCart();
      this.open();
    } catch (err) {
      console.error('Error adding form to cart:', err);
      alert('Não foi possível adicionar o produto ao carrinho agora.');
    }
  }

  async toggleProtection(isChecked, variantId) {
    if (!variantId || variantId.trim() === '') return;

    try {
      if (isChecked) {
        sessionStorage.removeItem('protection_declined');
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });
      } else {
        sessionStorage.setItem('protection_declined', 'true');
        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        const protItem = cart.items ? cart.items.find(it => String(it.id) === String(variantId) || String(it.variant_id) === String(variantId)) : null;
        if (protItem) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: protItem.key, quantity: 0 })
          });
        }
      }
      await this.refreshCart();
    } catch (err) {
      console.error('Error toggling protection product:', err);
    }
  }

  async checkAutoAddProtection(cart) {
    if (!cart || !cart.items || cart.items.length === 0) return;

    const protCheckbox = document.getElementById('cart-protection-checkbox');
    if (!protCheckbox) return;

    const variantId = protCheckbox.getAttribute('data-protection-variant');
    const autoAdd = protCheckbox.getAttribute('data-auto-add') !== 'false';
    const isDeclined = sessionStorage.getItem('protection_declined') === 'true';

    if (!isDeclined && autoAdd) {
      protCheckbox.checked = true;
      if (variantId && variantId.trim() !== '') {
        const hasProtection = cart.items.some(it => String(it.id) === String(variantId) || String(it.variant_id) === String(variantId));
        if (!hasProtection) {
          try {
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: variantId, quantity: 1 })
            });
            const newCartRes = await fetch('/cart.js');
            const newCart = await newCartRes.json();
            this.renderCart(newCart, true);
          } catch (e) {
            console.error('Auto-add protection failed:', e);
          }
        }
      } else {
        if (!cart.attributes || cart.attributes['Garantie Livraison Sérénité'] !== 'Oui (+2,99 €)') {
          fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              attributes: {
                'Garantie Livraison Sérénité': 'Oui (+2,99 €)'
              }
            })
          }).catch(console.error);
        }
      }
    }
  }

  async changeQuantity(lineKey, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: quantity })
      });

      const updatedCart = await response.json();
      this.renderCart(updatedCart);
    } catch (err) {
      console.error('Error changing cart quantity:', err);
    }
  }

  async refreshCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.renderCart(cart);
    } catch (err) {
      console.error('Error fetching cart:', err);
    }
  }

  renderCart(cart, skipAutoCheck = false) {
    if (!cart) return;

    // Refresh badge list in case new ones appeared
    const cartCountBadges = document.querySelectorAll('.cart-count-badge');
    const totalCount = cart.item_count || 0;
    const totalPrice = cart.total_price || 0;

    // Update badge counters
    cartCountBadges.forEach((badge) => {
      badge.textContent = totalCount;
      if (totalCount > 0) {
        badge.classList.remove('hidden');
        badge.style.display = 'flex';
      } else {
        badge.classList.add('hidden');
        badge.style.display = 'none';
      }
    });

    // Synchronize shipping protection checkbox state (Pre-checked by default unless declined)
    const protCheckbox = document.getElementById('cart-protection-checkbox');
    if (protCheckbox) {
      const isDeclined = sessionStorage.getItem('protection_declined') === 'true';
      if (isDeclined) {
        protCheckbox.checked = false;
      } else {
        protCheckbox.checked = true;
      }
    }

    // Synchronize cross-sell cards: mark button if product already in cart
    if (cart.items) {
      const crossSellCards = document.querySelectorAll('.cart-cross-sell-card');
      crossSellCards.forEach(card => {
        const btn = card.querySelector('.btn-quick-add');
        if (!btn) return;
        const varId = btn.getAttribute('data-variant-id');
        const isInCart = cart.items.some(it => String(it.id) === String(varId) || String(it.variant_id) === String(varId));
        if (isInCart) {
          btn.disabled = true;
          btn.classList.remove('bg-emerald-600', 'hover:bg-emerald-700');
          btn.classList.add('bg-emerald-50', 'text-emerald-800', 'border', 'border-emerald-200', 'cursor-default');
          btn.innerHTML = '<span>✓</span> <span>Adicionado</span>';
        } else {
          btn.disabled = false;
          btn.classList.remove('bg-emerald-50', 'text-emerald-800', 'border', 'border-emerald-200', 'cursor-default');
          btn.classList.add('bg-emerald-600', 'hover:bg-emerald-700');
          btn.innerHTML = '<span>+</span> <span>Adicionar</span>';
        }
      });
    }

    // Check if protection should be auto-added
    if (!skipAutoCheck) {
      this.checkAutoAddProtection(cart);
    }

    // Update Subtotal
    const subtotalEl = document.getElementById('cart-drawer-subtotal');
    if (subtotalEl) {
      subtotalEl.textContent = this.formatMoney(totalPrice);
    }

    // Update Shipping Bar
    this.updateShippingBar(totalPrice / 100);

    // Render items or empty state
    const itemsContainer = document.getElementById('cart-drawer-items');
    if (!itemsContainer) return;

    if (!cart.items || cart.items.length === 0) {
      itemsContainer.innerHTML = `
        <div class="text-center py-12">
          <div class="text-4xl mb-3">🛒</div>
          <p class="text-gray-500 font-medium mb-4">Seu carrinho está vazio.</p>
          <a href="/collections/all" class="btn-primary inline-flex py-2 px-5 text-sm">
            Explorar produtos
          </a>
        </div>
      `;
      return;
    }

    let itemsHtml = '';
    cart.items.forEach((item, index) => {
      const imgUrl = item.image || (item.featured_image ? (item.featured_image.url || item.featured_image) : '') || '';
      const itemTitle = item.product_title || item.title || 'Produto';
      const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      const linePrice = item.final_line_price != null ? item.final_line_price : (item.line_price || (item.price * item.quantity) || 0);

      itemsHtml += `
        <div class="cart-item-row" data-line="${index + 1}">
          ${imgUrl ? `<img src="${imgUrl}" alt="${itemTitle}" width="80" height="80" class="cart-item-thumb">` : `<div class="cart-item-thumb bg-gray-100 flex items-center justify-center text-xl">🌿</div>`}
          <div class="flex-grow min-w-0">
            <h4 class="text-sm font-semibold text-gray-900 truncate">${itemTitle}</h4>
            ${variantTitle ? `<p class="text-xs text-gray-500 truncate">${variantTitle}</p>` : ''}
            <div class="flex items-center justify-between mt-2">
              <div class="flex items-center border border-gray-200 rounded-lg overflow-hidden h-8">
                <button type="button" class="w-7 h-full flex items-center justify-center text-gray-600 hover:bg-gray-100" onclick="window.themeCart.changeQuantity('${item.key}', ${item.quantity - 1})">-</button>
                <span class="w-8 text-center text-xs font-semibold">${item.quantity}</span>
                <button type="button" class="w-7 h-full flex items-center justify-center text-gray-600 hover:bg-gray-100" onclick="window.themeCart.changeQuantity('${item.key}', ${item.quantity + 1})">+</button>
              </div>
              <span class="text-sm font-bold text-gray-900">${this.formatMoney(linePrice)}</span>
            </div>
          </div>
          <button type="button" class="text-gray-400 hover:text-red-500 p-1" title="Remover" onclick="window.themeCart.changeQuantity('${item.key}', 0)">
            ✕
          </button>
        </div>
      `;
    });

    itemsContainer.innerHTML = itemsHtml;
  }

  updateShippingBar(totalEuros) {
    const shippingProgressEl = document.getElementById('cart-shipping-progress');
    if (!shippingProgressEl) return;
    const threshold = this.freeShippingThreshold;
    const remaining = threshold - (totalEuros || 0);
    const progressEl = shippingProgressEl.querySelector('.progress-fill');
    const msgEl = shippingProgressEl.querySelector('.shipping-msg');

    if (threshold <= 0) {
      if (progressEl) progressEl.style.width = '100%';
      if (msgEl) msgEl.innerHTML = '🎉 <strong>Parabéns!</strong> Você ganhou frete grátis!';
      return;
    }

    if (remaining <= 0) {
      if (progressEl) progressEl.style.width = '100%';
      if (msgEl) msgEl.innerHTML = '🎉 <strong>Parabéns!</strong> Você ganhou frete grátis!';
    } else {
      const percentage = Math.min(100, Math.max(5, ((totalEuros || 0) / threshold) * 100));
      if (progressEl) progressEl.style.width = `${percentage}%`;
      const remainingText = remaining.toLocaleString('pt-BR', { style: 'currency', currency: this.currencyCode() });
      if (msgEl) msgEl.innerHTML = `Faltam <strong>${remainingText}</strong> para ganhar <strong>frete grátis</strong>!`;
    }
  }

  // Devise de la boutique (BRL pour SeletoMarket), jamais EUR en dur.
  currencyCode() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'BRL';
  }

  formatMoney(cents) {
    return ((cents || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: this.currencyCode() });
  }
}

// Resilient initialization
function initThemeCartEngine() {
  if (!window.themeCart) {
    window.themeCart = new CartDrawer();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initThemeCartEngine);
} else {
  initThemeCartEngine();
}

// Re-init on Shopify section reload (theme editor)
document.addEventListener('shopify:section:load', () => {
  window.themeCart = new CartDrawer();
});
